<template>
  <div class="storage-artists-tab">
    <b-card no-body class="mb-1">
      <b-card-body class="pb-50">
        <b-row>
          <b-col md="3" class="mb-1">
            <b-form-input
              v-model="filters.search"
              placeholder="Исполнитель"
              @keyup.enter="applyFilters"
            />
          </b-col>
          <b-col md="3" class="mb-1">
            <v-select
              :value="selectedMechanic"
              :options="mechanicOptions"
              label="name"
              :selectable="isMechanicSelectable"
              placeholder="Механика"
              @input="option => { filters.mechanic_id = option ? String(option.id) : '' }"
            >
              <template #option="option">
                <div v-if="option.isGroupHeader" class="mechanic-group-header">{{ option.name }}</div>
                <template v-else>{{ option.name }}</template>
              </template>
            </v-select>
          </b-col>
          <b-col md="2" class="mb-1">
            <v-select
              :value="selectedCluster"
              :options="clusterOptions"
              label="title"
              placeholder="Кластер"
              @input="option => { filters.cluster_id = option ? String(option.id) : '' }"
            />
          </b-col>
          <b-col md="2" class="mb-1">
            <v-select
              :value="selectedDecade"
              :options="decadeOptions"
              label="name"
              placeholder="Декада"
              @input="option => { filters.decade = option ? String(option.value) : '' }"
            />
          </b-col>
          <b-col md="2" class="mb-1">
            <b-input-group size="sm" prepend="Не играли с">
              <b-form-input v-model="filters.not_played_since" type="date" />
            </b-input-group>
          </b-col>
        </b-row>
        <div class="d-flex align-items-center mb-50">
          <b-button variant="primary" size="sm" class="mr-50" @click="applyFilters">Применить</b-button>
          <b-button variant="flat-secondary" size="sm" @click="resetFilters">Сбросить</b-button>
          <span class="ml-auto text-muted small">Найдено: {{ total }}</span>
        </div>
      </b-card-body>
    </b-card>

    <b-card no-body>
      <b-table
        :items="rows"
        :fields="tableColumns"
        responsive
        hover
        show-empty
        empty-text="Ничего не найдено"
        class="position-relative mb-0 storage-artists-table"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        no-local-sorting
        :busy="loading"
        @sort-changed="onSortChanged"
        @row-clicked="openArtistQuestions"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-50" />
            <strong>Загрузка...</strong>
          </div>
        </template>

        <template #cell(title)="data">
          <span class="font-weight-bold">{{ data.item.artist.title }}</span>
        </template>

        <template #cell(last_premiere_at)="data">
          {{ formatDate(data.item.last_premiere_at) }}
        </template>

        <template #cell(last_played_at)="data">
          {{ formatDate(data.item.last_played_at) }}
        </template>

        <template #cell(by_mechanic)="data">
          <span v-if="!data.item.by_mechanic.length" class="text-muted">—</span>
          <!-- Ссылка внутри ячейки не вызывает row-clicked (b-table пропускает клики по интерактивным элементам) -->
          <b-link
            v-for="entry in data.item.by_mechanic"
            :key="`${data.item.artist.id}-${entry.mechanic_id}`"
            :to="questionsRoute(data.item, entry.mechanic_id === null ? 'none' : String(entry.mechanic_id))"
            class="mr-50 mb-25 d-inline-block"
            :title="`Вопросов: ${entry.count} — открыть`"
          >
            <b-badge variant="light-primary">
              {{ entry.name || 'без механики' }} {{ formatMonth(entry.last_premiere_at) }}
            </b-badge>
          </b-link>
        </template>
      </b-table>

      <b-card-body class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <label class="text-nowrap mb-0 mr-50 font-small-3">На странице:</label>
          <v-select
            :value="perPage"
            :options="perPageOptions"
            :clearable="false"
            style="min-width: 80px"
            @input="onPerPageInput"
          />
        </div>
        <b-pagination
          v-if="total > 0"
          :value="page"
          :total-rows="total"
          :per-page="perPage"
          first-number
          last-number
          class="mb-0"
          prev-class="prev-item"
          next-class="next-item"
          @input="onPageInput"
        />
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BBadge, BButton, BCard, BCardBody, BCol, BFormInput, BInputGroup, BLink, BPagination, BRow, BSpinner, BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { computed, ref, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import { isMechanicSelectable, toVueSelectOptions } from './mechanicGroups'

const MODULE = 'question-storage'
const TAB = 'artists'
// По умолчанию «давно не играли» сверху (ArtistPlaysQuery::DEFAULT_SORT)
const DEFAULT_SORT = 'last_premiere_at'
const DEFAULT_PER_PAGE = 30

// Ключи = query-параметры GET question-storage/artists (ArtistPlaysQuery::paginate)
const getDefaultFilters = () => ({
  search: '',
  mechanic_id: '',
  cluster_id: '',
  decade: '',
  not_played_since: '',
})

const formatDate = value => {
  if (!value) return '—'
  const [year, month, day] = String(value).slice(0, 10).split('-')

  return `${day}.${month}.${year}`
}

const formatMonth = value => {
  if (!value) return '—'
  const [year, month] = String(value).slice(0, 7).split('-')

  return `${month}.${year}`
}

export default {
  components: {
    BBadge, BButton, BCard, BCardBody, BCol, BFormInput, BInputGroup, BLink, BPagination, BRow, BSpinner, BTable, vSelect,
  },
  setup(props, { root }) {
    const toast = useToast()
    const showError = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'AlertTriangleIcon', variant: 'danger' },
    })

    const filters = ref(getDefaultFilters())
    const page = ref(1)
    const perPage = ref(DEFAULT_PER_PAGE)
    const sort = ref(DEFAULT_SORT)
    const rows = ref([])
    const total = ref(0)
    const loading = ref(false)
    const lookups = ref({})
    let fetchNumber = 0

    const perPageOptions = [30, 50, 100]
    // Ключи сортируемых колонок = значения sort на бэке
    const tableColumns = [
      { key: 'title', label: 'Исполнитель', sortable: true },
      { key: 'questions_count', label: 'Вопросов', sortable: true },
      { key: 'last_premiere_at', label: 'Последняя премьера', sortable: true },
      { key: 'last_played_at', label: 'Последний отыгрыш', sortable: true },
      { key: 'by_mechanic', label: 'По механикам (последняя премьера)' },
    ]

    store.dispatch(`${MODULE}/fetchLookups`)
      .then(response => { lookups.value = response.data.data || {} })
      .catch(() => showError('Не удалось загрузить справочники'))

    // Механики с несбираемыми заголовками групп
    const mechanicOptions = computed(() => toVueSelectOptions(lookups.value.mechanics || []))
    const decadeOptions = computed(() => lookups.value.decades || [])
    const clusterOptions = computed(() => lookups.value.clusters || [])

    const selectedMechanic = computed(() => mechanicOptions.value.find(mechanic => String(mechanic.id) === filters.value.mechanic_id) || null)
    const selectedDecade = computed(() => decadeOptions.value.find(decade => String(decade.value) === filters.value.decade) || null)
    const selectedCluster = computed(() => clusterOptions.value.find(cluster => String(cluster.id) === filters.value.cluster_id) || null)

    const buildParams = () => {
      const params = { page: page.value, per_page: perPage.value, sort: sort.value }
      Object.entries(filters.value).forEach(([key, value]) => {
        if (value !== '') params[key] = value
      })

      return params
    }

    // --- URL ↔ состояние (как на вкладке «Вопросы»): назад в браузере восстанавливает фильтры ---
    let lastQueryKey = null
    // Механика из применённых фильтров — её передаём во вкладку «Вопросы»
    const appliedMechanicId = ref('')
    const queryKey = query => JSON.stringify(Object.keys(query).sort().map(key => [key, String(query[key])]))

    const readFromQuery = query => {
      const nextFilters = getDefaultFilters()
      Object.keys(nextFilters).forEach(key => {
        if (query[key] !== undefined && query[key] !== null) nextFilters[key] = String(query[key])
      })
      filters.value = nextFilters
      page.value = parseInt(query.page, 10) || 1
      perPage.value = perPageOptions.includes(parseInt(query.per_page, 10)) ? parseInt(query.per_page, 10) : DEFAULT_PER_PAGE
      sort.value = query.sort ? String(query.sort) : DEFAULT_SORT
    }

    const buildUrlQuery = () => {
      const query = { tab: TAB }
      Object.entries(buildParams()).forEach(([key, value]) => { query[key] = String(value) })
      if (query.page === '1') delete query.page
      if (query.per_page === String(DEFAULT_PER_PAGE)) delete query.per_page
      if (query.sort === DEFAULT_SORT) delete query.sort

      return query
    }

    const fetchList = () => {
      const urlQuery = buildUrlQuery()
      lastQueryKey = queryKey(urlQuery)
      if (queryKey(root.$route.query) !== lastQueryKey) root.$router.replace({ query: urlQuery }).catch(() => {})
      appliedMechanicId.value = filters.value.mechanic_id

      fetchNumber += 1
      const requestNumber = fetchNumber
      loading.value = true
      store.dispatch(`${MODULE}/fetchArtists`, buildParams())
        .then(response => {
          if (requestNumber !== fetchNumber) return
          rows.value = response.data.data || []
          total.value = response.data.total || 0
        })
        .catch(() => {
          if (requestNumber === fetchNumber) showError('Не удалось загрузить исполнителей')
        })
        .finally(() => {
          if (requestNumber === fetchNumber) loading.value = false
        })
    }

    const applyFilters = () => {
      page.value = 1
      fetchList()
    }

    const resetFilters = () => {
      filters.value = getDefaultFilters()
      applyFilters()
    }

    const onPageInput = value => {
      page.value = value
      fetchList()
    }

    const onPerPageInput = value => {
      perPage.value = value
      applyFilters()
    }

    const sortBy = computed(() => sort.value.replace(/^-/, ''))
    const sortDesc = computed(() => sort.value.startsWith('-'))
    const onSortChanged = context => {
      if (!context.sortBy) return
      sort.value = `${context.sortDesc ? '-' : ''}${context.sortBy}`
      applyFilters()
    }

    readFromQuery(root.$route.query)
    fetchList()

    watch(() => root.$route.query, query => {
      if (root.$route.name !== 'question-storage' || query.tab !== TAB) return
      if (queryKey(query) === lastQueryKey) return
      readFromQuery(query)
      fetchList()
    })

    // Вкладка «Вопросы»: этот исполнитель, только сыгранные (сводка считает только их), механика — если задана
    const questionsRoute = (item, mechanicId) => {
      const query = {
        tab: 'questions', artist_id: String(item.artist.id), artist_name: item.artist.title, played: '1',
      }
      if (mechanicId) query.mechanic_id = mechanicId

      return { name: 'question-storage', query }
    }

    const openArtistQuestions = item => {
      root.$router.push(questionsRoute(item, appliedMechanicId.value)).catch(() => {})
    }

    return {
      isMechanicSelectable,
      filters,
      page,
      perPage,
      perPageOptions,
      rows,
      total,
      loading,
      tableColumns,
      mechanicOptions,
      decadeOptions,
      clusterOptions,
      selectedMechanic,
      selectedDecade,
      selectedCluster,
      applyFilters,
      resetFilters,
      onPageInput,
      onPerPageInput,
      sortBy,
      sortDesc,
      onSortChanged,
      openArtistQuestions,
      questionsRoute,
      formatDate,
      formatMonth,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style scoped>
.storage-artists-table >>> tbody tr {
  cursor: pointer;
}
.mechanic-group-header {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  cursor: default;
}
</style>
